import {
  type Cell,
  type RowData,
  type Table as TableInstance,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";

interface DataTableCellProps {
  className?: string;
  onClick?: () => void;
}

interface DataTableProps<TData extends RowData> {
  table: TableInstance<TData>;
  noResultsLabel: string;
  getCellProps?: (
    cell: Cell<TData, unknown>,
    rowData: TData,
  ) => DataTableCellProps | undefined;
}

export const DataTable = <TData extends RowData>({
  table,
  noResultsLabel,
  getCellProps,
}: DataTableProps<TData>) => (
  <Table className="bg-white">
    <TableHeader className="bg-white">
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHead key={header.id}>
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
            </TableHead>
          ))}
        </TableRow>
      ))}
    </TableHeader>
    <TableBody>
      {table.getRowModel().rows.length ? (
        table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => {
              const cellProps = getCellProps?.(cell, row.original);

              return (
                <TableCell
                  key={cell.id}
                  className={cellProps?.className}
                  onClick={cellProps?.onClick}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell
            colSpan={table.getVisibleLeafColumns().length}
            className="h-24 text-center"
          >
            {noResultsLabel}
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
);
